import { useSyncExternalStore, type ReactElement } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import "../../ui/kit/styles.ts";
import "./moderation.css";
import { KitInputProvider, type KitInput } from "../../ui/kit/index.ts";
import { moderationClient } from "./client.ts";
import type { ModerationAsk } from "./command.ts";
import { ModerationDesk } from "./desk.ts";
import { familyClient } from "./family-client.ts";
import { FamilyModel } from "./family.ts";
import { moderationFeed, type ModerationFeed } from "./feed.ts";
import { lookupClient } from "./lookup-client.ts";
import { LookupModel } from "./lookup.ts";
import { ModerationNoticeLayer } from "./notice.tsx";
import { ModerationNotices } from "./notices.ts";
import { onlineClient } from "./online-client.ts";
import { OnlineModel, type OnlineTimers } from "./online.ts";
import { staffClient } from "./staff-client.ts";
import { StaffModel } from "./staff.ts";
import { ModerationWindow, type ModerationWindowStart } from "./window.tsx";

/**
 * The game page's moderation surface (P1.4-T9, ADR-056's amendment of 2026-10-04): a React root of its own in the
 * page's `#moderation` host, over the game menu and under the confirm dialog's `#modal`, on the page's input
 * router. It draws the Moderation window while the desk is open and the notice a player is shown, and draws
 * neither while the page is out of the world, since the host stacks over the loading shell: a freeze told at a
 * join waits for the world to be revealed, and nothing holds the keyboard behind the shell.
 */

/**
 * What the surface needs from the page: its input router, the GM's desk, the list of who is online, the read of the
 * family the window is on, the family lookup, the player's notices, the GM's own family when the page knows it, and where the window
 * starts (the gallery's states).
 */
export interface ModerationSurface {
  readonly input: KitInput;
  readonly desk: ModerationDesk;
  readonly online: OnlineModel;
  readonly family: FamilyModel;
  readonly lookup: LookupModel;
  readonly staff: StaffModel;
  readonly notices: ModerationNotices;
  readonly self?: () => string | null;
  readonly start?: ModerationWindowStart;
}

/**
 * The page's own timers, which the list of who is online runs on.
 */
export const PAGE_TIMERS: OnlineTimers = {
  now: () => Date.now(),
  after: (ms, run) => {
    const timer = setTimeout(run, ms);
    return () => clearTimeout(timer);
  },
};

/**
 * Keeps the list of who is online asking only while the window is open and the tab visible (ADR-056's amendment of
 * 2026-10-06, decision 5): it follows the desk's open and close and the document's visibility.
 *
 * @param desk The desk.
 * @param online The list.
 * @param page The document whose visibility counts, or null where there is none.
 * @returns Stops following. The page's surface follows for the page's lifetime and never calls it; a test that mounts
 *   and unmounts does.
 */
export function followDesk(desk: ModerationDesk, online: OnlineModel, page: Document | null): () => void {
  let open = desk.getSnapshot().open;
  if (open) {
    online.open();
  }
  const stop = desk.subscribe(() => {
    const now = desk.getSnapshot().open;
    if (now !== open) {
      open = now;
      if (now) {
        online.open();
      } else {
        online.close();
      }
    }
  });
  /**
   * Tells the list whether the tab shows.
   */
  const seen = (): void => online.shown(page?.visibilityState !== "hidden");
  page?.addEventListener("visibilitychange", seen);
  return () => {
    stop();
    page?.removeEventListener("visibilitychange", seen);
  };
}

/**
 * The surface as the page drives it.
 */
export interface ModerationControl {
  /** Tells the surface the world is revealed, so it draws from now on. */
  reveal(): void;
  /**
   * Tells the surface the page left the world: the window closes and nothing is drawn, before this returns, so
   * nothing it gives focus back to as it goes can take focus from the notice that follows.
   */
  conceal(): void;
}

/**
 * Mounts the surface in its host, drawing nothing until the world is revealed.
 *
 * @param host The page's `#moderation` element in the overlay root, positioned over the page and taking no pointer
 *   events itself; a scrim fills it and takes them while the window or a warning shows.
 * @param surface The router, the desk and the notices.
 * @returns The surface's control.
 * @example
 * const moderation = mountModeration(document.getElementById("moderation") as HTMLElement, { input, desk, notices });
 * moderation.reveal();
 */
export function mountModeration(host: HTMLElement, surface: ModerationSurface): ModerationControl {
  let revealed = false;
  const listeners = new Set<() => void>();
  const root = createRoot(host);

  /**
   * Adds a listener for whether the world is revealed.
   *
   * @param listener Called when it changes.
   * @returns A function that removes it.
   */
  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  /**
   * Whether the world is revealed.
   *
   * @returns True between a reveal and the next conceal.
   */
  const snapshot = (): boolean => revealed;

  /**
   * Sets whether the world is revealed and tells the view when it changed.
   *
   * @param next Whether it is.
   */
  const set = (next: boolean): void => {
    if (next === revealed) {
      return;
    }
    revealed = next;
    for (const listener of listeners) {
      listener();
    }
  };

  /**
   * The window and the notice, while the world is revealed.
   *
   * @returns Both inside the router's provider, or null while the page is out of the world.
   */
  function View(): ReactElement | null {
    const shown = useSyncExternalStore(subscribe, snapshot, snapshot);
    if (!shown) {
      return null;
    }
    return (
      <KitInputProvider input={surface.input}>
        <ModerationWindow desk={surface.desk} online={surface.online} family={surface.family} lookup={surface.lookup} staff={surface.staff} {...(surface.self === undefined ? {} : { self: surface.self })} {...(surface.start === undefined ? {} : { start: surface.start })} />
        <ModerationNoticeLayer notices={surface.notices} />
      </KitInputProvider>
    );
  }

  root.render(<View />);
  followDesk(surface.desk, surface.online, typeof document === "undefined" ? null : document);
  return {
    reveal: () => set(true),
    conceal: () => {
      surface.desk.close();
      flushSync(() => set(false));
    },
  };
}

/**
 * What the page's moderation runs with: the overlay host, the input router, the gateway's `/moderation` URL, the gateway's `/staff` URL, the
 * page's current session token (null when it holds none), and where a line goes in the chat log, as plain
 * information (`note`) or as an error (`fail`), and the GM's own family when the page knows it (`self`), which the window
 * says an action is recorded as.
 */
export interface ModerationPageOptions {
  readonly host: HTMLElement;
  readonly input: KitInput;
  readonly endpoint: string;
  readonly staffEndpoint: string;
  readonly token: () => string | null;
  readonly note: (text: string) => void;
  readonly fail: (text: string) => void;
  readonly self?: () => string | null;
}

/**
 * The page's moderation, whole: what the scene is given, what the chat input hands a GM command to, and the
 * surface's control.
 */
export interface ModerationPage extends ModerationControl {
  /** The feed the scene takes as its `moderation` option. */
  readonly feed: ModerationFeed;
  /**
   * Takes a GM command's request from the chat input.
   *
   * @param ask The request.
   * @returns False when the gateway offered this page nothing; true when the desk took it.
   */
  readonly ask: (ask: ModerationAsk) => boolean;
  /**
   * Tells a listener whenever the player's notices change, a freeze's coming and going among them, so a surface that
   * follows the freeze (the game menu's Change look item) can ask the feed again.
   *
   * @param listener Called on each change.
   * @returns A function that removes it.
   */
  readonly watchNotices: (listener: () => void) => () => void;
  /**
   * Tells a listener whenever a freeze begins or ends, whatever notice is showing, so a surface that closes on a
   * freeze hears one that arrives behind a warning.
   *
   * @param listener Called on each change.
   * @returns A function that removes it.
   */
  readonly watchFreeze: (listener: () => void) => () => void;
}

/**
 * Builds the page's moderation and mounts its surface: the notices, whose lines go to the chat log; the client of
 * the gateway's `/moderation`; the desk, whose answers go to the chat log as information or as errors; and the
 * feed over both. A page with no account session is offered nothing by the gateway, so its desk takes no command
 * and opens no window.
 *
 * @param options The host, the router, the endpoint, the token and the chat log's lines.
 * @returns The feed, the chat input's hand-over and the surface's control.
 * @example
 * const moderation = startModeration({ host, input, endpoint: `${origin}/moderation`, token: () => session?.sessionToken ?? null, note: (text) => chat.note(text), fail: (text) => chat.fail(text) });
 */
export function startModeration(options: ModerationPageOptions): ModerationPage {
  const notices = new ModerationNotices({ say: options.note });
  const desk = new ModerationDesk({
    client: moderationClient({ endpoint: options.endpoint, token: options.token }),
    say: (text, kind) => (kind === "error" ? options.fail(text) : options.note(text)),
  });
  const online = new OnlineModel({ fetch: onlineClient({ endpoint: `${options.endpoint}/online`, token: options.token }), timers: PAGE_TIMERS });
  const family = new FamilyModel(familyClient({ endpoint: `${options.endpoint}/family`, token: options.token }));
  const lookup = new LookupModel({ fetch: lookupClient({ endpoint: `${options.endpoint}/lookup`, token: options.token }), timers: PAGE_TIMERS });
  const staff = new StaffModel(staffClient({ accountsEndpoint: `${options.staffEndpoint}/accounts`, designationEndpoint: `${options.staffEndpoint}/designation`, token: options.token }));
  const control = mountModeration(options.host, { input: options.input, desk, online, family, lookup, staff, notices, ...(options.self === undefined ? {} : { self: options.self }) });
  return { feed: moderationFeed(notices, desk), ask: (ask) => desk.ask(ask), watchNotices: notices.subscribe, watchFreeze: notices.watchFreeze, reveal: control.reveal, conceal: control.conceal };
}
